import React, { useState, useEffect, useRef, useMemo } from 'react';
import CardHand from './CardHand';
import { effectiveMusicVolume } from '../utils/settings';
import '../styles/Social.css';

/**
 * FellowshipCombatInterface — Multi-player / multi-monster turn-based combat
 * 
 * Shows initiative order bar, current turn indicator, 60s turn timer,
 * all monsters with HP bars, and the active player's action menu.
 */
const FellowshipCombatInterface = ({ battleState, character, socket, chatMessages = [], onTrack }) => {
  const track = onTrack || (() => {}); // no-op fallback
  const [actionMode, setActionMode] = useState('main');
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [pendingSpell, setPendingSpell] = useState(null);
  const [pendingAbility, setPendingAbility] = useState(null);
  const [chatText, setChatText] = useState('');
  const logRef = useRef(null);
  const chatEndRef = useRef(null);
  const battleAudioRef = useRef(new Audio());
  const prevLogLengthRef = useRef(0);
  const [glowingIndices, setGlowingIndices] = useState(new Set());
  const actionSentRef = useRef(false); // Prevents double-fire during network latency
  const actionTimeoutRef = useRef(null); // Safety timeout for actionSentRef
  const glowTimersRef = useRef([]); // Track glow timeouts for cleanup

  // Reset action lock when battle state changes (server responded)
  useEffect(() => {
    actionSentRef.current = false;
    if (actionTimeoutRef.current) {
      clearTimeout(actionTimeoutRef.current);
      actionTimeoutRef.current = null;
    }
  }, [battleState?.currentTurn, battleState?.round, battleState?.log?.length]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => { if (actionTimeoutRef.current) clearTimeout(actionTimeoutRef.current); };
  }, []);

  // Cleanup glow timers on unmount
  useEffect(() => {
    return () => {
      glowTimersRef.current.forEach(t => clearTimeout(t));
      glowTimersRef.current = [];
    };
  }, []);

  // Battle music: play battle.mp3 on mount, switch to win/death on end, cleanup on unmount
  useEffect(() => {
    const audio = battleAudioRef.current;
    if (!audio) return;

    let musicFile = 'battle.mp3';
    if (battleState?.status === 'victory') musicFile = 'winning.mp3';
    else if (battleState?.status === 'defeat') musicFile = 'dead.mp3';

    const musicPath = `/music/${musicFile}`;
    const fullPath = window.location.origin + musicPath;

    if (audio.src !== fullPath) {
      audio.src = musicPath;
      audio.volume = effectiveMusicVolume();
      audio.loop = (battleState?.status === 'active');
      audio.play().catch(() => {});
    }

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [battleState?.status]);

  const isMyTurn = battleState?.currentTurn?.characterId === character?.id;
  const myBattle = battleState?.combatants?.find(c => c.type === 'player' && c.characterId === character?.id)?.battle;
  const myDeck = myBattle?.deck || null;

  // Helper: set action lock + safety timeout (auto-resets after 2s if server never responds)
  const startActionLock = () => {
    actionSentRef.current = true;
    if (actionTimeoutRef.current) clearTimeout(actionTimeoutRef.current);
    actionTimeoutRef.current = setTimeout(() => {
      actionSentRef.current = false;
      actionTimeoutRef.current = null;
    }, 2000);
  };

  // ── Deck-Building Card System Handlers ──
  const handlePlayCard = (cardId) => {
    if (!socket || !isMyTurn || actionSentRef.current) return;
    const card = myDeck?.hand?.find(c => c.id === cardId);
    track('f_card_play', `${card?.name || '?'} (id:${cardId} cost:${card?.apCost ?? '?'}AP)`);
    startActionLock();
    socket.emit('fellowship_combat_action', {
      type: 'play_card',
      cardId,
      targetMonster: selectedTarget || aliveMonsters[0]?.monsterId
    });
  };

  const handleDeckEndTurn = () => {
    if (!socket || !isMyTurn || actionSentRef.current) return;
    track('f_btn_end_turn', `R${battleState?.round || '?'}`);
    startActionLock();
    socket.emit('fellowship_combat_action', { type: 'end_turn' });
  };

  const handleScryResolve = (topIds, bottomIds) => {
    if (!socket || !isMyTurn || actionSentRef.current) return;
    startActionLock();
    socket.emit('fellowship_combat_action', { type: 'scry_resolve', topIds, bottomIds });
  };

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [battleState?.log]);

  // Track new monster action entries and apply glow effect for 3 seconds
  useEffect(() => {
    const log = battleState?.log || [];
    const prevLen = prevLogLengthRef.current;
    if (log.length > prevLen) {
      const newIndices = new Set();
      for (let i = prevLen; i < log.length; i++) {
        const entry = log[i];
        const isMonsterTurn = entry.startsWith('👹');
        const isMonsterAction = entry.startsWith('[') && entry.includes('→');
        if (isMonsterTurn || isMonsterAction) {
          newIndices.add(i);
        }
      }
      if (newIndices.size > 0) {
        setGlowingIndices(prev => {
          const merged = new Set(prev);
          newIndices.forEach(idx => merged.add(idx));
          return merged;
        });
        // Remove glow after 3 seconds
        const timer = setTimeout(() => {
          setGlowingIndices(prev => {
            const next = new Set(prev);
            newIndices.forEach(idx => next.delete(idx));
            return next;
          });
        }, 3000);
        glowTimersRef.current.push(timer);
      }
    }
    prevLogLengthRef.current = log.length;
  }, [battleState?.log]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Reset action mode when it becomes my turn
  useEffect(() => {
    if (isMyTurn) {
      setActionMode('main');
      setSelectedTarget(null);
      setPendingSpell(null);
      setPendingAbility(null);
    }
  }, [isMyTurn, battleState?.currentTurn]);

  // Keyboard shortcuts for deck mode
  useEffect(() => {
    if (!isMyTurn || !myDeck) return;
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const key = e.key.toLowerCase();
      if (key === 'e' || key === ' ') { e.preventDefault(); handleDeckEndTurn(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMyTurn, myDeck]);

  const aliveMonsters = useMemo(() =>
    (battleState?.combatants || []).filter(c => c.type === 'monster' && c.monster.currentHp > 0),
    [battleState?.combatants]);
  const players = useMemo(() =>
    (battleState?.combatants || []).filter(c => c.type === 'player'),
    [battleState?.combatants]);
  const alivePlayers = useMemo(() => players.filter(p => p.hp > 0 && !p.fled), [players]);
  const deadPlayers = useMemo(() => players.filter(p => p.hp <= 0 && !p.fled), [players]);
  const otherAlivePlayers = useMemo(() => alivePlayers.filter(p => p.characterId !== character?.id), [alivePlayers, character?.id]);

  // Spell types that can target allies
  const ALLY_TARGET_TYPES = ['heal', 'buff_att', 'buff_def', 'stat_up', 'hot', 'combo_buff'];

  // Check if a spell is ally-targetable (heal/buff type)
  const isAllySpell = (spell) => {
    if (!spell) return false;
    // Check by spell type from battle data
    if (spell.type && ALLY_TARGET_TYPES.includes(spell.type)) return true;
    // Fallback: check common spell name patterns
    const name = (spell.name || '').toLowerCase();
    return name.includes('heal') || name.includes('cure') || name.includes('bless') ||
           name.includes('shield') || name.includes('virtue') || name.includes('rejuvenation') ||
           name.includes('fortify') || name.includes('haste') || name.includes('regenerat');
  };

  // v246: Check if a spell is AoE (hits all enemies, no target selection needed)
  const isAoeSpell = (spell) => {
    if (!spell) return false;
    return spell.type === 'aoe_damage' || spell.fellowshipOnly;
  };

  // v246: Check if an ability is Guard (needs alive ally target)
  const isGuardAbility = (ability) => {
    return ability && ability.effect === 'guard';
  };

  const doAction = (action) => {
    if (!socket || !isMyTurn || actionSentRef.current) return;
    track('f_action', `${action.type}${action.spellName ? ': ' + action.spellName : ''}${action.abilityName ? ': ' + action.abilityName : ''}${action.targetAlly ? ' →ally:' + action.targetAlly : ''}`);
    startActionLock();
    socket.emit('fellowship_combat_action', {
      ...action,
      targetMonster: selectedTarget || aliveMonsters[0]?.monsterId
    });
    setPendingSpell(null);
    setPendingAbility(null);
  };

  // Handle selecting a spell — if it's a heal/buff, show ally selector
  const handleSpellClick = (spell) => {
    if (isAllySpell(spell) && alivePlayers.length > 1) {
      setPendingSpell(spell);
      setActionMode('select_ally');
    } else if (isAoeSpell(spell)) {
      // AoE spells don't need target selection — they hit all enemies
      doAction({ type: 'spell', spellName: spell.name });
    } else {
      doAction({ type: 'spell', spellName: spell.name });
    }
  };

  // Handle selecting resurrection ability — show dead ally selector
  const handleAbilityClick = (ability) => {
    if (ability.effect === 'resurrect' && deadPlayers.length > 0) {
      setPendingAbility(ability);
      setActionMode('select_dead_ally');
    } else if (isGuardAbility(ability) && otherAlivePlayers.length > 0) {
      // v246: Guard — select an alive ally to protect
      setPendingAbility(ability);
      setActionMode('select_guard_ally');
    } else if (ability.name === 'Taunt') {
      doAction({ type: 'ability', abilityName: ability.name });
    } else {
      doAction({ type: 'ability', abilityName: ability.name });
    }
  };

  // Select an ally for heal/buff spell
  const handleAllySelect = (allyId) => {
    if (pendingSpell) {
      doAction({ type: 'spell', spellName: pendingSpell.name, targetAlly: allyId });
    }
    setActionMode('main');
  };

  // Select a dead ally for resurrect
  const handleDeadAllySelect = (allyId) => {
    if (pendingAbility) {
      doAction({ type: 'ability', abilityName: pendingAbility.name, targetAlly: allyId });
    }
    setActionMode('main');
  };

  // v246: Select an alive ally for Guard
  const handleGuardAllySelect = (allyId) => {
    if (pendingAbility) {
      doAction({ type: 'ability', abilityName: pendingAbility.name, targetAlly: allyId });
    }
    setActionMode('main');
  };

  const lastChatTimeRef = useRef(0);
  const handleSendChat = (e) => {
    e.preventDefault();
    if (!chatText.trim() || !socket) return;
    // Throttle chat to 1 message per 500ms
    const now = Date.now();
    if (now - lastChatTimeRef.current < 500) return;
    lastChatTimeRef.current = now;
    socket.emit('fellowship_chat', { message: chatText.trim() });
    setChatText('');
  };

  const currentCombatantName = battleState?.currentTurn?.type === 'player'
    ? battleState.currentTurn.characterName
    : battleState?.currentTurn?.monsterName;

  const formatTimestamp = (ts) => {
    const d = new Date(ts);
    return `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
  };

  if (!battleState) return null;

  return (
    <div className="fellowship-combat-overlay">
      <div className="fellowship-combat-container">
        {/* Victory/Defeat banner */}
        {battleState.status !== 'active' && (
          <div className={`fc-result-banner ${battleState.status}`}>
            {battleState.status === 'victory' ? '⚔️ VICTORY!' : '💀 DEFEAT'}
          </div>
        )}

        {/* Initiative Order Bar */}
        <div className="fc-initiative-bar">
          <span className="fc-round-label">Round {battleState.round}</span>
          <div className="fc-initiative-list">
            {(battleState.combatants || []).map((c, i) => {
              const isCurrent = (battleState.currentTurn?.characterId === c.characterId && c.type === 'player') ||
                                (battleState.currentTurn?.monsterName === c.monster?.name && c.type === 'monster');
              const isDead = c.type === 'player' ? (c.hp <= 0 || c.fled) : (c.monster?.currentHp <= 0);
              return (
                <div key={i} className={`fc-init-slot ${isCurrent ? 'current' : ''} ${isDead ? 'dead' : ''} ${c.type}`}>
                  <span className="fc-init-icon">{c.type === 'player' ? '🛡️' : '👹'}</span>
                  <span className="fc-init-name">
                    {c.type === 'player' ? c.characterName : c.monster?.name}
                  </span>
                  <span className="fc-init-roll">{c.initiative}</span>
                </div>
              );
            })}
          </div>
          {battleState.status === 'active' && (
            <div className="fc-timer">
              ⏱️ {battleState.turnTimeRemaining || 0}s
            </div>
          )}
        </div>

        <div className="fc-main-area">
          {/* Monsters Display */}
          <div className="fc-monsters">
            {/* v640.1: Aggregate threat meter (per-player; enemies share one threat table) */}
            {(() => {
              const threatPlayers = (battleState.combatants || [])
                .filter(c => c.type === 'player' && c.hp > 0)
                .map(p => ({
                  id: p.characterId,
                  name: p.characterName,
                  threat: (p.threatLevel || 0) + (p.healerThreat || 0),
                }));
              const totalThreat = threatPlayers.reduce((s, p) => s + p.threat, 0);
              if (threatPlayers.length < 2 || totalThreat <= 0) return null;
              threatPlayers.sort((a, b) => b.threat - a.threat);
              return (
                <div className="fc-threat-meter" style={{
                  background: '#1a1a2e', border: '1px solid #5a3a3a', borderRadius: 4,
                  padding: '4px 8px', marginBottom: 6, fontSize: '0.78em', color: '#d8b8b8'
                }} title="Aggro: enemies prefer high-threat targets">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span>⚔️ Threat</span>
                    <span style={{ color: '#ff8a8a' }}>Top: {threatPlayers[0].name}</span>
                  </div>
                  {threatPlayers.map(p => {
                    const pct = totalThreat > 0 ? Math.round((p.threat / totalThreat) * 100) : 0;
                    const isYou = p.id === character?.id;
                    return (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 1 }}>
                        <span style={{ minWidth: 70, color: isYou ? '#ffd28a' : '#bbb' }}>{p.name}{isYou ? ' (You)' : ''}</span>
                        <div style={{ flex: 1, background: '#2a1a1a', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: isYou ? '#ffb84a' : '#cc5555' }} />
                        </div>
                        <span style={{ minWidth: 30, textAlign: 'right' }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {(battleState.combatants || []).filter(c => c.type === 'monster').map((c, i) => {
              const m = c.monster;
              const hpPct = Math.max(0, (m.currentHp / m.hp) * 100);
              const isTarget = selectedTarget === c.monsterId;
              const isDead = m.currentHp <= 0;
              return (
                <div
                  key={c.monsterId}
                  className={`fc-monster-card ${isTarget ? 'targeted' : ''} ${isDead ? 'dead' : ''}`}
                  onClick={() => !isDead && setSelectedTarget(c.monsterId)}
                >
                  <div className="fc-monster-pic">
                    <img
                      src={`/images/monsters/${m.pic}`}
                      alt={m.name}
                      onError={(e) => { e.target.src = '/images/monsters/default.jpg'; }}
                    />
                    {isDead && <div className="fc-monster-dead-overlay">💀</div>}
                  </div>
                  <div className="fc-monster-info">
                    <div className="fc-monster-name">{m.name} (Lv.{m.level})</div>
                    <div className="fc-hp-bar-container">
                      <div className="fc-hp-bar" style={{ width: `${hpPct}%` }} />
                      <span className="fc-hp-text">{m.currentHp}/{m.hp}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Players Status */}
          <div className="fc-players">
            {players.map(p => {
              const hpPct = Math.max(0, (p.hp / p.maxHp) * 100);
              const mpPct = p.maxMp > 0 ? Math.max(0, (p.mp / p.maxMp) * 100) : 0;
              const isCurrent = battleState.currentTurn?.characterId === p.characterId;
              const pet = p.pet;
              const petHpPct = pet ? Math.max(0, (pet.currentHp / pet.hp) * 100) : 0;
              return (
                <div key={p.characterId} className={`fc-player-card ${isCurrent ? 'active-turn' : ''} ${p.hp <= 0 ? 'dead' : ''} ${p.fled ? 'fled' : ''}`}>
                  <div className="fc-player-name">
                    {isCurrent && <span className="fc-turn-arrow">▶</span>}
                    {p.characterName}
                    {p.characterId === character?.id && <span className="fc-you-tag">(You)</span>}
                  </div>
                  <div className="fc-bar-row">
                    <div className="fc-bar-container hp">
                      <div className="fc-bar" style={{ width: `${hpPct}%` }} />
                      <span className="fc-bar-text">{p.hp}/{p.maxHp}</span>
                    </div>
                    {p.maxMp > 0 && (
                      <div className="fc-bar-container mp">
                        <div className="fc-bar" style={{ width: `${mpPct}%` }} />
                        <span className="fc-bar-text">{p.mp}/{p.maxMp}</span>
                      </div>
                    )}
                  </div>
                  {/* Pet status */}
                  {pet && (
                    <div className={`fc-pet-status ${pet.currentHp <= 0 ? 'dead' : ''}`}>
                      <span className="fc-pet-icon">🐾</span>
                      <span className="fc-pet-name">{pet.name}</span>
                      {pet.element && <span className="fc-pet-element">({pet.element})</span>}
                      <div className="fc-bar-container pet-hp">
                        <div className="fc-bar" style={{ width: `${petHpPct}%` }} />
                        <span className="fc-bar-text">{pet.currentHp}/{pet.hp}</span>
                      </div>
                      {pet.currentHp <= 0 && <span className="fc-pet-dead">💀</span>}
                      {p.petCommand === 'guard' && <span className="fc-pet-command">🛡️</span>}
                      {p.petCommand === 'pounce' && <span className="fc-pet-command">🐆</span>}
                    </div>
                  )}
                  {p.fled && <span className="fc-fled-tag">Fled</span>}
                  {p.hp <= 0 && !p.fled && <span className="fc-dead-tag">💀</span>}
                </div>
              );
            })}
          </div>

          {/* Current Turn Indicator */}
          <div className="fc-turn-indicator">
            {battleState.status === 'active' ? (
              isMyTurn ? (
                <span className="fc-your-turn">⚔️ Your Turn!</span>
              ) : (
                <span className="fc-waiting">Waiting for {currentCombatantName}...</span>
              )
            ) : null}
          </div>

          {/* Action Buttons — only when it's my turn */}
          {isMyTurn && battleState.status === 'active' && myBattle && (
            <div className="fc-actions">
              {/* ── DECK MODE: Card Hand replaces classic action buttons ── */}
              {myDeck && actionMode === 'main' && (
                <div className="fc-deck-panel">
                  <CardHand
                    deckState={myDeck}
                    character={character}
                    onPlayCard={handlePlayCard}
                    onEndTurn={handleDeckEndTurn}
                    onScryResolve={handleScryResolve}
                    isPlayerTurn={isMyTurn}
                    onTrack={track}
                  />
                  <div className="fc-deck-utility">
                    {(myBattle.inventory || []).filter(item => item.type === 'consumable' || item.usable).length > 0 && (
                      <button className="fc-action-btn items" onClick={() => setActionMode('items')}>
                        🎒 Items
                      </button>
                    )}
                    {!battleState?.noRun && (
                      <button className="fc-action-btn flee" onClick={() => doAction({ type: 'flee' })}>
                        🏃 Flee
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── CLASSIC MODE: Original action buttons (when no deck system) ── */}
              {!myDeck && actionMode === 'main' && (
                <div className="fc-action-row">
                  <button className="fc-action-btn attack" onClick={() => doAction({ type: 'attack' })}>
                    ⚔️ Attack
                  </button>
                  {(myBattle.availableSpells?.length > 0) && (
                    <button className="fc-action-btn spells" onClick={() => setActionMode('spells')}>
                      ✨ Spells
                    </button>
                  )}
                  {(myBattle.availableAbilities?.length > 0) && (
                    <button className="fc-action-btn abilities" onClick={() => setActionMode('abilities')}>
                      💥 Abilities
                    </button>
                  )}
                  {(myBattle.inventory?.length > 0) && (
                    <button className="fc-action-btn items" onClick={() => setActionMode('items')}>
                      🎒 Items
                    </button>
                  )}
                  {!battleState?.noRun && (
                    <button className="fc-action-btn flee" onClick={() => doAction({ type: 'flee' })}>
                      🏃 Flee
                    </button>
                  )}
                </div>
              )}

              {actionMode === 'spells' && (
                <div className="fc-sub-actions">
                  <button className="fc-back-btn" onClick={() => setActionMode('main')}>← Back</button>
                  <div className="fc-spell-list">
                    {(myBattle.availableSpells || []).map((spell, i) => (
                      <button
                        key={i}
                        className={`fc-spell-btn ${isAllySpell(spell) ? 'ally-spell' : ''} ${isAoeSpell(spell) ? 'aoe-spell' : ''}`}
                        disabled={(spell.mp || spell.mpCost || 0) > (myBattle.character?.currentMp ?? 0)}
                        onClick={() => handleSpellClick(spell)}
                      >
                        <span className="fc-spell-name">{spell.name}</span>
                        <span className="fc-spell-cost">{spell.mp || spell.mpCost} MP</span>
                        {isAllySpell(spell) && <span className="fc-ally-icon" title="Can target allies">👥</span>}
                        {isAoeSpell(spell) && <span className="fc-aoe-icon" title="Hits all enemies">💥</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {actionMode === 'abilities' && (
                <div className="fc-sub-actions">
                  <button className="fc-back-btn" onClick={() => setActionMode('main')}>← Back</button>
                  <div className="fc-spell-list">
                    {(myBattle.availableAbilities || []).map((ability, i) => (
                      <button
                        key={i}
                        className={`fc-spell-btn ${ability.name === 'Taunt' ? 'taunt-btn' : ''} ${ability.effect === 'resurrect' ? 'resurrect-btn' : ''} ${ability.effect === 'guard' ? 'guard-btn' : ''} ${ability.fellowshipOnly ? 'fellowship-only' : ''}`}
                        onClick={() => handleAbilityClick(ability)}
                        disabled={(ability.effect === 'resurrect' && deadPlayers.length === 0) || (ability.effect === 'guard' && otherAlivePlayers.length === 0)}
                        title={ability.effect === 'resurrect' && deadPlayers.length === 0 ? 'No fallen allies to resurrect' : ability.effect === 'guard' && otherAlivePlayers.length === 0 ? 'No allies to guard' : ability.description || ''}
                      >
                        {ability.name === 'Taunt' && '🛡️ '}
                        {ability.effect === 'resurrect' && '✨ '}
                        {ability.effect === 'guard' && '🛡️ '}
                        {ability.effect === 'cleave' && '⚔️ '}
                        {ability.effect === 'whirlwind' && '🌪️ '}
                        {ability.name}
                        {ability.fellowshipOnly && <span className="fc-fellowship-icon" title="Fellowship combat only"> ⚔️</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Ally target selection for heal/buff spells */}
              {actionMode === 'select_ally' && pendingSpell && (
                <div className="fc-sub-actions">
                  <button className="fc-back-btn" onClick={() => { setActionMode('spells'); setPendingSpell(null); }}>← Back</button>
                  <div className="fc-ally-select-label">Cast {pendingSpell.name} on:</div>
                  <div className="fc-ally-select-list">
                    {alivePlayers.map(p => (
                      <button
                        key={p.characterId}
                        className={`fc-ally-btn ${p.characterId === character?.id ? 'self' : ''}`}
                        onClick={() => handleAllySelect(p.characterId)}
                      >
                        <span className="fc-ally-name">
                          {p.characterName}
                          {p.characterId === character?.id && ' (Self)'}
                        </span>
                        <span className="fc-ally-hp">{p.hp}/{p.maxHp} HP</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Dead ally selection for Resurrection */}
              {actionMode === 'select_dead_ally' && pendingAbility && (
                <div className="fc-sub-actions">
                  <button className="fc-back-btn" onClick={() => { setActionMode('abilities'); setPendingAbility(null); }}>← Back</button>
                  <div className="fc-ally-select-label">{pendingAbility.name}:</div>
                  <div className="fc-ally-select-list">
                    {deadPlayers.map(p => (
                      <button
                        key={p.characterId}
                        className="fc-ally-btn dead-ally"
                        onClick={() => handleDeadAllySelect(p.characterId)}
                      >
                        <span className="fc-ally-name">💀 {p.characterName}</span>
                        <span className="fc-ally-hp">Fallen</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* v246: Guard ally selection */}
              {actionMode === 'select_guard_ally' && pendingAbility && (
                <div className="fc-sub-actions">
                  <button className="fc-back-btn" onClick={() => { setActionMode('abilities'); setPendingAbility(null); }}>← Back</button>
                  <div className="fc-ally-select-label">🛡️ Guard — protect who?</div>
                  <div className="fc-ally-select-list">
                    {otherAlivePlayers.map(p => (
                      <button
                        key={p.characterId}
                        className="fc-ally-btn guard-ally"
                        onClick={() => handleGuardAllySelect(p.characterId)}
                      >
                        <span className="fc-ally-name">{p.characterName}</span>
                        <span className="fc-ally-hp">{p.hp}/{p.maxHp} HP</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {actionMode === 'items' && (
                <div className="fc-sub-actions">
                  <button className="fc-back-btn" onClick={() => setActionMode('main')}>← Back</button>
                  <div className="fc-spell-list">
                    {/* Server sends restore/scroll/buff/redraw; the old 'consumable' filter matched nothing. */}
                    {(myBattle.inventory || [])
                      .filter(item => ['restore', 'scroll', 'buff', 'redraw'].includes(item.type) || item.usable)
                      .map((item, i) => {
                        const apCost = myBattle.deck ? (item.apCost || 1) : 0;
                        const unaffordable = apCost > 0 && (myBattle.deck?.ap ?? 0) < apCost;
                        return (
                          <button
                            key={i}
                            className={`fc-spell-btn ${unaffordable ? 'unaffordable' : ''}`}
                            disabled={unaffordable}
                            onClick={() => doAction({ type: 'item', item: item.name })}
                          >
                            {item.name} {item.quantity > 1 ? `(${item.quantity})` : ''}
                            {apCost > 0 && <span className="item-ap-cost">{apCost} AP</span>}
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Monster target selector (when multiple alive) */}
              {aliveMonsters.length > 1 && (
                <div className="fc-target-select">
                  <span className="fc-target-label">Target:</span>
                  {aliveMonsters.map(m => (
                    <button
                      key={m.monsterId}
                      className={`fc-target-btn ${selectedTarget === m.monsterId ? 'selected' : ''}`}
                      onClick={() => setSelectedTarget(m.monsterId)}
                    >
                      {m.monster.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Fellowship Synergy Indicator — shows recent combo triggers from the log */}
          {(() => {
            const log = battleState?.log || [];
            const recentSynergies = [];
            // Scan last 10 entries for synergy messages
            for (let i = Math.max(0, log.length - 10); i < log.length; i++) {
              if (log[i] && log[i].includes('FELLOWSHIP SYNERGY')) {
                recentSynergies.push(log[i]);
              }
            }
            if (recentSynergies.length === 0) return null;
            const latest = recentSynergies[recentSynergies.length - 1];
            return (
              <div className="fc-synergy-banner">
                <span className="fc-synergy-icon">🔗</span>
                <span className="fc-synergy-text">{latest.replace(/^✨ FELLOWSHIP SYNERGY: /, '')}</span>
              </div>
            );
          })()}

          {/* Battle Log */}
          <div className="fc-log" ref={logRef}>
            {(battleState.log || []).map((entry, i) => {
              const isHeader = entry.startsWith('===');
              const isMonsterTurn = entry.startsWith('👹');
              const isMonsterAction = entry.startsWith('[') && entry.includes('→');
              const isRoundMarker = entry.startsWith('--- Round');
              const isGlowing = glowingIndices.has(i);
              return (
                <div key={i} className={`fc-log-entry ${isHeader ? 'fc-log-header' : ''} ${isMonsterTurn ? 'fc-log-monster-turn' : ''} ${isMonsterAction ? 'fc-log-monster-action' : ''} ${isRoundMarker ? 'fc-log-round' : ''} ${isGlowing ? 'fc-log-glow' : ''}`}>
                  {entry}
                </div>
              );
            })}
          </div>

          {/* Fellowship Chat (accessible during combat) */}
          <div className="fc-chat">
            <div className="fc-chat-label">Fellowship Chat</div>
            <div className="fc-chat-messages">
              {chatMessages
                .filter(m => m.chatType === 'fellowship')
                .slice(-20)
                .map((msg, i) => (
                  <div key={i} className="fc-chat-msg">
                    <span className="fc-chat-sender">{msg.characterName}:</span>
                    <span className="fc-chat-text">{msg.message}</span>
                  </div>
                ))}
              <div ref={chatEndRef} />
            </div>
            <form onSubmit={handleSendChat} className="fc-chat-input">
              <input
                type="text"
                value={chatText}
                onChange={e => setChatText(e.target.value)}
                placeholder="Chat with fellowship..."
              />
              <button type="submit" disabled={!chatText.trim()}>Send</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FellowshipCombatInterface;
